/**
 * Event naming: object_verb (e.g., estimate_submitted, pdf_export_initiated)
 * TypeScript enforces that every event has a properties mapping.
 */

import { ESTIMATE_EVENTS, type EstimateEventPropertiesMap } from "./estimate.events";
import { EXPORT_EVENTS, type ExportEventPropertiesMap } from "./export.events";
import { LINE_ITEM_EVENTS, type LineItemEventPropertiesMap } from "./line-item.events";
import { MIT_TO_RECON_EVENTS, type MitToReconEventPropertiesMap } from "./mit-to-recon.events";
import { PHOTO_EVENTS, type PhotoEventPropertiesMap } from "./photo.events";
import { ROOM_EVENTS, type RoomEventPropertiesMap } from "./room.events";
import type { AssertAllEventsMapped } from "../types";

export const ANALYTICS_EVENTS = {
  ESTIMATE_CREATION_STARTED: ESTIMATE_EVENTS.CREATION_STARTED,
  ESTIMATE_SUBMITTED: ESTIMATE_EVENTS.SUBMITTED,
  ESTIMATE_CANCEL_INITIATED: ESTIMATE_EVENTS.CANCEL_INITIATED,
  ESTIMATE_CANCEL_ABORTED: ESTIMATE_EVENTS.CANCEL_ABORTED,
  ESTIMATE_DISCARDED: ESTIMATE_EVENTS.DISCARDED,
  ADDITIONAL_UPLOAD_STARTED: ESTIMATE_EVENTS.ADDITIONAL_UPLOAD_STARTED,
  MIT_TO_RECON_ESTIMATE_GENERATED: MIT_TO_RECON_EVENTS.GENERATED,
  XACTIMATE_EXPORT_INITIATED: EXPORT_EVENTS.XACTIMATE_EXPORT_INITIATED,
  PDF_EXPORT_INITIATED: EXPORT_EVENTS.PDF_EXPORT_INITIATED,
  ROOM_CREATE_INITIATED: ROOM_EVENTS.CREATE_INITIATED,
  ROOM_CREATE_CANCELLED: ROOM_EVENTS.CREATE_CANCELLED,
  ROOM_CREATED: ROOM_EVENTS.CREATED,
  ROOM_ARCHIVE_INITIATED: ROOM_EVENTS.ARCHIVE_INITIATED,
  ROOM_ARCHIVE_CANCELLED: ROOM_EVENTS.ARCHIVE_CANCELLED,
  ROOM_ARCHIVED: ROOM_EVENTS.ARCHIVED,
  PROJECT_ARCHIVE_INITIATED: ESTIMATE_EVENTS.PROJECT_ARCHIVE_INITIATED,
  PROJECT_ARCHIVE_CANCELLED: ESTIMATE_EVENTS.PROJECT_ARCHIVE_CANCELLED,
  PROJECT_ARCHIVED: ESTIMATE_EVENTS.PROJECT_ARCHIVED,
  LINE_ITEM_REORDERED: LINE_ITEM_EVENTS.REORDERED,
  PHOTO_ROOM_REASSIGN_INITIATED: PHOTO_EVENTS.ROOM_REASSIGN_INITIATED,
  PHOTO_ROOM_REASSIGNED: PHOTO_EVENTS.ROOM_REASSIGNED,
  PHOTO_ROOM_REASSIGN_FAILED: PHOTO_EVENTS.ROOM_REASSIGN_FAILED,
  PHOTO_LABELS_APPLY_INITIATED: PHOTO_EVENTS.LABELS_APPLY_INITIATED,
  PHOTO_LABELS_APPLIED: PHOTO_EVENTS.LABELS_APPLIED,
  PHOTO_LABELS_APPLY_FAILED: PHOTO_EVENTS.LABELS_APPLY_FAILED,
  PHOTO_LABELS_REMOVED: PHOTO_EVENTS.LABELS_REMOVED,
  PHOTO_DELETE_INITIATED: PHOTO_EVENTS.DELETE_INITIATED,
  PHOTO_DELETED: PHOTO_EVENTS.DELETED,
  PHOTO_DELETE_FAILED: PHOTO_EVENTS.DELETE_FAILED,
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export interface AnalyticsEventPropertiesMap
  extends EstimateEventPropertiesMap,
    ExportEventPropertiesMap,
    LineItemEventPropertiesMap,
    MitToReconEventPropertiesMap,
    PhotoEventPropertiesMap,
    RoomEventPropertiesMap {}

// Compile-time validation that all events are mapped
const _validateMapping: AssertAllEventsMapped<typeof ANALYTICS_EVENTS, AnalyticsEventPropertiesMap> =
  {} as AnalyticsEventPropertiesMap;
void _validateMapping;
